<script>
export const FAMILY_MEMBER_HEALTH_SCHEMA = {
    widgetType: 'familyMemberHistory',
    label: 'Family Member Health History',
    fields: [
        { name: 'condition', type: 'string', required: true, description: 'Health condition (e.g. Diabetes, Heart disease)' },
        { name: 'relationship', type: 'string', required: true, description: 'Family member relationship (e.g. Mother, Father, Brother)' },
    ],
};
</script>

<script setup>
import { ref, computed, watch, onMounted, inject, onBeforeUnmount } from 'vue';
import DataProvider from '@/components/DataProvider';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { FamilyMemberHistory, List } from '@/fhirworks';
import { getFamilyMemberTypes } from '@/common/api/terminology.api';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import { useDisplay } from 'vuetify';
import { capitalize } from 'lodash';
import { ValidateEach } from '@vuelidate/components';
import { required } from '@vuelidate/validators';
const vuetifyDisplay = useDisplay();

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    title: String,
    patientId: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
    linkId: String,
});

const familyMemberHistoryDP = ref(null);
const familyMemberHistoryData = ref([]);
const relationshipOptions = ref([]);
const bnDialogRef = ref(null);
const hasPriorConditions = ref(null);

onMounted(() => {
    getFamilyMemberTypes().then((relationship) => (relationshipOptions.value = relationship.map((item) => item.resource)));
});

const viewOnly = computed(() => props.isLocked || props.printView);

const getRelationshipDisplay = computed(() => {
    return relationshipOptions.value.map((i) => ({ title: capitalize(i.display), value: i.code }));
});

const familyMemberHistoryQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: [
            { query: `patient=${props.patientId}`, resourceType: 'FamilyMemberHistory' },
            { query: `subject=${props.patientId}&code=familymemberhistory`, resourceType: 'List' },
        ],
    };
});

watch(
    () => hasPriorConditions.value,
    () => {
        if (hasPriorConditions.value) {
            setPriorConditionsTrue();
        } else {
            setPriorConditionsFalse();
        }
    },
);

const conditions = computed(() => {
    return familyMemberHistoryData.value.filter((condition) => condition.resourceType === 'FamilyMemberHistory');
});

const conditionsLoaded = async () => {
    if (familyMemberHistoryData.value.length) {
        hasPriorConditions.value = !familyMemberHistoryData.value.find((list) => list.resourceType === 'List' && list.emptyReason?.coding?.[0]?.code === 'nilknown');
    }
};

watch(
    () => familyMemberHistoryData.value,
    () => {
        if (familyMemberHistoryData.value && !props.isLocked) {
            emit('update:modelValue', familyMemberHistoryData.value);
        }
    },
    { immediate: true, deep: true },
);
const setPriorConditionsTrue = () => {
    let nilKnown = familyMemberHistoryData.value.find((list) => list.resourceType === 'List' && list.emptyReason?.coding?.[0]?.code === 'nilknown');
    if (nilKnown) familyMemberHistoryDP.value.deleteItem(nilKnown);
    if (!familyMemberHistoryData.value.find((resource) => resource.resourceType === 'FamilyMemberHistory')) {
        addEmptyFamMemberHistory();
    }
};
const setPriorConditionsFalse = async () => {
    let noKnownResource = new List({
        subject: { id: props.patientId, resourceType: 'Patient' },
        status: 'current',
        mode: 'working',
        code: {
            coding: [
                {
                    system: 'http://bestnotes.com',
                    code: 'familymemberhistory',
                },
            ],
        },
        emptyReason: {
            coding: [
                {
                    system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
                    code: 'nilknown',
                    display: 'Nil Known',
                },
            ],
        },
    });

    if (familyMemberHistoryData.value.find((data) => data.resourceType === 'List' && data.emptyReason?.coding?.[0]?.code === 'nilknown')) return;

    // Skip dialog if no saved family member history exists
    // Also skip dialog if there's only 1 condition with blank text
    const hasNoSavedHistory = !familyMemberHistoryData.value.find((data) => data.resourceType === 'FamilyMemberHistory' && data?.id);
    const hasSingleBlankCondition = conditions.value.length === 1 && !conditions.value[0]?.condition?.[0]?.code?.text;

    if (hasNoSavedHistory || hasSingleBlankCondition) {
        for (let resource of familyMemberHistoryData.value) {
            familyMemberHistoryDP.value.deleteItem(resource);
        }
        await familyMemberHistoryDP.value.save(noKnownResource);
        return;
    }

    const options = {
        header: 'Warning',
        message: 'This will delete all family member history conditions, continue?',
        confirm: 'Delete',
        cancel: 'Cancel',
    };

    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) {
            hasPriorConditions.value = true;
            return;
        }
        familyMemberHistoryData.value.forEach((resource) => {
            familyMemberHistoryDP.value.deleteItem(resource);
        });
        await familyMemberHistoryDP.value.save(noKnownResource);
    });
};

const removeFamMemberHistory = (condition) => {
    const options = {
        header: 'Delete condition',
        message: 'Are you sure you want to delete this family member condition?',
        confirm: 'Delete',
        cancel: 'Cancel',
    };

    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        await familyMemberHistoryDP.value.deleteItem(condition);
        if (!familyMemberHistoryData.value.length) {
            addEmptyFamMemberHistory();
        }
    });
};

const addEmptyFamMemberHistory = () => {
    let emptyHistory = new FamilyMemberHistory({
        patient: { id: props.patientId, resourceType: 'Patient' },
        status: 'health-unknown',
        relationship: { text: '' },
        condition: [{ code: { text: '' } }],
    });
    familyMemberHistoryDP.value.addItem(emptyHistory);
};

const updateRelationshipSelection = (condition, val) => {
    if (!val) {
        if (condition.id) {
            familyMemberHistoryDP.value.deleteItem(condition);
            if (!conditions.value.length || conditions.value.every((c) => c === condition)) {
                addEmptyFamMemberHistory();
            }
        } else {
            condition.relationship = { text: '' };
            condition.condition = [{ code: { text: '' } }];
        }
        return;
    }
    let famMember = relationshipOptions.value.find((item) => item.code === val);
    if (condition.relationship?.coding?.[0]) {
        condition.relationship.coding[0].code = val;
        condition.relationship.coding[0].system = famMember.system;
        return;
    }
    condition.relationship = { coding: [{ code: val, system: famMember.system }] };
    condition.status = 'completed';
};

const updateCondition = (condition, val) => {
    if (!condition.condition?.[0]) {
        condition.condition = [{ code: { text: val } }];
    } else if (!condition.condition[0].code) {
        condition.condition[0].code = { text: val };
    } else {
        condition.condition[0].code.text = val;
    }
    condition.status = 'completed';
};

const canAutosave = (item) => {
    if (item.resourceType === 'FamilyMemberHistory') {
        return !!item.relationship?.coding?.[0]?.code && !!item.condition?.[0]?.code?.text;
    }
    return true;
};

const conditionValidationRules = { relationship: { required }, conditionText: { required } };

const getConditionState = (condition) => ({
    relationship: condition.relationship?.coding?.[0]?.code || '',
    conditionText: condition.condition?.[0]?.code?.text || '',
});

const addItem = async (data) => {
    const record = new FamilyMemberHistory({
        patient: { id: props.patientId, resourceType: 'Patient' },
        status: 'completed',
        relationship: { text: data.relationship || '' },
        condition: [{ code: { text: data.condition || '' } }],
    });

    if (data.relationship) {
        const match = relationshipOptions.value.find((item) => capitalize(item.display) === data.relationship || item.code === data.relationship);
        if (match) {
            record.relationship = { coding: [{ code: match.code, system: match.system }] };
        }
    }

    const nilKnownList = familyMemberHistoryData.value.find((list) => list.resourceType === 'List' && list.emptyReason?.coding?.[0]?.code === 'nilknown');
    if (nilKnownList) {
        familyMemberHistoryDP.value.deleteItem(nilKnownList);
    }

    await familyMemberHistoryDP.value.save(record);
    hasPriorConditions.value = true;
    return { success: true };
};

const getItems = () => {
    return conditions.value
        .filter((c) => c.condition?.[0]?.code?.text)
        .map((c) => ({
            condition: c.condition?.[0]?.code?.text || '',
            relationship: capitalize(relationshipOptions.value.find((r) => r.code === c.relationship?.coding?.[0]?.code)?.display || c.relationship?.text || ''),
        }));
};

defineExpose({ addItem, getItems });

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('familyMemberHistory', { addItem, getItems, schema: FAMILY_MEMBER_HEALTH_SCHEMA });
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('familyMemberHistory');
});
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2" :data-linkid="linkId">
        <DataProvider
            ref="familyMemberHistoryDP"
            v-model="familyMemberHistoryData"
            collection
            refresh-on-change
            autosave
            :autosave-when="canAutosave"
            v-bind="familyMemberHistoryQuery"
            @loaded="conditionsLoaded"
        >
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn variant="flat" color="primary" @click="refresh">Try again</v-btn>
                </div>
            </template>
            <template #default>
                <div>
                    <div class="d-flex py-1 text-h2" style="color: #43a047">
                        <FontAwesomeIcon size="lg" :icon="['far', 'list-timeline']" class="align-self-center mr-2"></FontAwesomeIcon>
                        <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
                    </div>
                    <v-radio-group v-model="hasPriorConditions" label="Does your family have any prior conditions?" :readonly="props.isLocked" inline class="mt-0">
                        <v-radio label="Yes" :value="true" class="mr-2" data-cy="priorConditionsTrue"></v-radio>
                        <v-radio label="No" :value="false" data-cy="priorConditionsFalse"></v-radio>
                    </v-radio-group>
                    <template v-if="hasPriorConditions">
                        <div data-cy="famHistoryConditionsList">
                            <ValidateEach v-for="(condition, index) in conditions" :key="index" :state="getConditionState(condition)" :rules="conditionValidationRules">
                                <template #default="{ v }">
                                    <div style="border-left: 3px solid #43a047; opacity: 1; padding-left: 8px" class="d-flex flex-wrap mb-2 py-2">
                                        <v-text-field
                                            :model-value="condition.condition?.[0]?.code?.text"
                                            label="Condition"
                                            :readonly="viewOnly"
                                            class="order-1 ml-2 mr-4 w-25"
                                            :style="vuetifyDisplay.xs.value ? 'flex-basis: 100%; margin-bottom: 8px;' : 'flex-basis: auto;'"
                                            data-cy="famMemberCondition"
                                            v-bind="$bnVuelidateErrorExtractor(v, 'conditionText', true)"
                                            hide-details
                                            @update:model-value="(val) => updateCondition(condition, val)"
                                            @blur="v.conditionText.$touch"
                                        ></v-text-field>
                                        <v-autocomplete
                                            label="Family member"
                                            :clearable="!viewOnly"
                                            :items="getRelationshipDisplay"
                                            :data-options="JSON.stringify(getRelationshipDisplay)"
                                            data-linkid="famMemberSelect"
                                            :model-value="condition.relationship?.coding?.[0]?.code"
                                            :readonly="viewOnly"
                                            class="order-2 w-25"
                                            data-cy="famMemberSelect"
                                            v-bind="$bnVuelidateErrorExtractor(v, 'relationship', true)"
                                            hide-details
                                            @update:model-value="(val) => updateRelationshipSelection(condition, val)"
                                            @blur="v.relationship.$touch"
                                        >
                                            <template #item="{ item, props: itemProps }">
                                                <v-list-item v-bind="itemProps" :title="null" data-linkid-option="famMemberSelect">
                                                    {{ item.title }}
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                        <BnMenu v-if="!viewOnly" class="order-3" data-cy="removeHistoryMenu">
                                            <BnMenuItem
                                                :disabled="familyMemberHistoryData.length === 1"
                                                icon="trash"
                                                label="Delete condition"
                                                data-cy="removeHistoryButton"
                                                @click="removeFamMemberHistory(condition)"
                                            />
                                        </BnMenu>
                                    </div>
                                </template>
                            </ValidateEach>
                            <v-btn
                                v-if="!props.isLocked && !viewOnly"
                                :disabled="conditions.some((e) => e.condition?.[0]?.code?.text === '')"
                                color="primary"
                                variant="tonal"
                                @click="addEmptyFamMemberHistory()"
                            >
                                Add another condition
                                <template #prepend>
                                    <FontAwesomeIcon :icon="['far', 'plus']"></FontAwesomeIcon>
                                </template>
                            </v-btn>
                        </div>
                    </template>
                </div>
            </template>
        </DataProvider>
        <BnDialog ref="bnDialogRef" />
    </v-card>
</template>
